import React from "react";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import SelfImprovementOutlinedIcon from "@mui/icons-material/SelfImprovementOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import FitnessCenterOutlinedIcon from "@mui/icons-material/FitnessCenterOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";

export interface CategoryItem {
  id: string;
  name: string;
  subtitle: string;
  tag: string;
  icon: string;
  isOffer?: boolean;
  enabled: boolean;
  order?: number;
}

export const DEFAULT_CATEGORIES: CategoryItem[] = [
  {
    id: "hair-care",
    name: "Hair Care",
    subtitle: "Stylers & Dryers",
    tag: "Salon Grade",
    icon: "hair",
    isOffer: false,
    enabled: true,
    order: 1,
  },
  {
    id: "personal-care",
    name: "Personal Care",
    subtitle: "Massagers & Belts",
    tag: "Relief & Care",
    icon: "personal",
    isOffer: false,
    enabled: true,
    order: 2,
  },
  {
    id: "wellness-health",
    name: "Wellness & Health",
    subtitle: "Smart Devices",
    tag: "Certified",
    icon: "wellness",
    isOffer: false,
    enabled: true,
    order: 3,
  },
  {
    id: "refurbished-tech",
    name: "Refurbished Tech",
    subtitle: "Tested & Certified",
    tag: "Refurbished",
    icon: "tech",
    isOffer: false,
    enabled: true,
    order: 4,
  },
  {
    id: "special-offers",
    name: "Special Offers",
    subtitle: "Up to 50% Savings",
    tag: "SALE",
    icon: "offers",
    isOffer: true,
    enabled: true,
    order: 5,
  },
];

export const AVAILABLE_ICONS = [
  { key: "hair", label: "Hair & Spa", Icon: SpaOutlinedIcon },
  { key: "personal", label: "Personal Care", Icon: SelfImprovementOutlinedIcon },
  { key: "wellness", label: "Wellness & Health", Icon: FitnessCenterOutlinedIcon },
  { key: "tech", label: "Tech & Laptops", Icon: LaptopMacOutlinedIcon },
  { key: "offers", label: "Special Offers / SALE", Icon: LocalOfferOutlinedIcon },
  { key: "devices", label: "Devices & Gadgets", Icon: DevicesOutlinedIcon },
  { key: "bag", label: "Shopping Bag", Icon: ShoppingBagOutlinedIcon },
  { key: "sparkle", label: "Beauty & Sparkle", Icon: AutoAwesomeOutlinedIcon },
  { key: "diamond", label: "Luxury / Premium", Icon: DiamondOutlinedIcon },
  { key: "general", label: "General Category", Icon: CategoryOutlinedIcon },
];

export const renderCategoryIcon = (iconKey?: string, sx: any = { fontSize: 32 }) => {
  switch (iconKey?.toLowerCase()) {
    case "hair":
    case "spa":
      return <SpaOutlinedIcon sx={sx} />;
    case "personal":
    case "body":
      return <SelfImprovementOutlinedIcon sx={sx} />;
    case "wellness":
    case "health":
    case "fitness":
      return <FitnessCenterOutlinedIcon sx={sx} />;
    case "tech":
    case "laptop":
      return <LaptopMacOutlinedIcon sx={sx} />;
    case "offers":
    case "offer":
    case "sale":
      return <LocalOfferOutlinedIcon sx={sx} />;
    case "devices":
      return <DevicesOutlinedIcon sx={sx} />;
    case "bag":
      return <ShoppingBagOutlinedIcon sx={sx} />;
    case "sparkle":
      return <AutoAwesomeOutlinedIcon sx={sx} />;
    case "diamond":
      return <DiamondOutlinedIcon sx={sx} />;
    default:
      return <CategoryOutlinedIcon sx={sx} />;
  }
};

const LOCAL_STORAGE_KEY = "rudrix_store_categories";

export const normalizeCategories = (cats: any[]): CategoryItem[] => {
  if (!Array.isArray(cats)) return DEFAULT_CATEGORIES;

  return cats.map((cat, idx) => {
    // If string was stored
    if (typeof cat === "string") {
      const isOffer = cat.toLowerCase().includes("offer") || cat.toLowerCase().includes("sale");
      return {
        id: `cat-${idx}-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`,
        name: cat,
        subtitle: isOffer ? "Up to 50% Savings" : "Curated Collection",
        tag: isOffer ? "SALE" : "",
        icon: isOffer ? "offers" : "general",
        isOffer: isOffer,
        enabled: true,
        order: idx + 1,
      };
    }

    const name = cat.name || cat.title || "Category";
    const isOffer =
      cat.isOffer !== undefined
        ? Boolean(cat.isOffer)
        : name.toLowerCase().includes("offer") || name.toLowerCase().includes("sale");

    return {
      id: cat.id || `cat-${idx}-${name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`,
      name: name,
      subtitle: cat.subtitle || (isOffer ? "Up to 50% Savings" : ""),
      tag: cat.tag || (isOffer ? "SALE" : ""),
      icon: cat.icon || (isOffer ? "offers" : "general"),
      isOffer: isOffer,
      enabled: cat.enabled !== undefined ? Boolean(cat.enabled) : true,
      order: cat.order !== undefined ? cat.order : idx + 1,
    };
  });
};

export const getStoreCategories = (storeData?: any): CategoryItem[] => {
  try {
    // 1. Check storeData.categories
    if (storeData?.categories) {
      const cats =
        typeof storeData.categories === "string"
          ? JSON.parse(storeData.categories)
          : storeData.categories;
      if (Array.isArray(cats) && cats.length > 0) {
        return normalizeCategories(cats);
      }
    }

    // 2. Check storeData.header?.categories
    if (storeData?.header?.categories) {
      const cats =
        typeof storeData.header.categories === "string"
          ? JSON.parse(storeData.header.categories)
          : storeData.header.categories;
      if (Array.isArray(cats) && cats.length > 0) {
        return normalizeCategories(cats);
      }
    }

    // 3. Check localStorage
    if (typeof window !== "undefined" && window.localStorage) {
      const storeId = storeData?._id || storeData?.id;
      const key = storeId ? `${LOCAL_STORAGE_KEY}_${storeId}` : LOCAL_STORAGE_KEY;
      const saved = localStorage.getItem(key) || localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return normalizeCategories(parsed);
        }
      }
    }
  } catch (error) {
    console.error("Failed to parse store categories, using defaults:", error);
  }

  return DEFAULT_CATEGORIES;
};

export const saveCategoriesLocally = (categories: CategoryItem[], storeId?: string): void => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(categories));
      if (storeId) {
        localStorage.setItem(`${LOCAL_STORAGE_KEY}_${storeId}`, JSON.stringify(categories));
      }
    }
  } catch (error) {
    console.error("Failed to save categories locally:", error);
  }
};
